
import { CircularProgress, IconButton } from "@mui/material";
import React, { Fragment, memo, useContext, useEffect, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { useInView } from "react-intersection-observer";
import { RequestStatus, IContext, IPaging, ErrorToString } from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import AnimateFrom from "../../animations/AnimateFrom";
import { ITreeViewRecord, TreeViewCommand, TreeViewContext } from "../../treeView/TreeView";

interface IAPIHookResult<R, P> {
    data: R | null,
    status: RequestStatus,
    err: Error | unknown | null,
    send: (context: IContext, parameters: P, callBack: (results: R | null) => void) => void;
}

interface IParametersWithPaging {
    paging?: IPaging;
}

export type PagedDataView<P extends IParametersWithPaging, R> = {
    apiHook: () => IAPIHookResult<R[], P>,
    parameters: P,
    renderItem: (item: R, index: number) => React.ReactNode,
    header?: (hook: IAPIHookResult<R[], P>, records: R[]) => React.ReactNode,
    animate?: boolean,
    animateDelay?: number,
    isTable?: boolean,
    paged?: boolean,
    pagingLimit?: number,
    showError?: boolean,
    maintainRecordsAfterParamChange?: boolean;
};

const DelayedProgressIndicator = () => {
    let cancellation: NodeJS.Timeout;
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        cancellation = setTimeout(() => {
            setVisible(true);
        }, 500);
        return () => {
            if (cancellation != null) {
                clearTimeout(cancellation);
            }
        }
    }, []);
    if (visible == true) {
        return <div style={{ textAlign: "center" }}><CircularProgress></CircularProgress></div>;
    } else {
        return <Fragment />
    }
}

function component<P extends IParametersWithPaging, R>(props: React.PropsWithChildren<PagedDataView<P, R>>) {
    const session = useContext(SessionContext);

    const maxPagingLimit = props.pagingLimit == null ? 250 : props.pagingLimit;
    let limit = maxPagingLimit;

    if (session.current.site) {
        if (session.current.site.preferences) {
            if (!isNaN(limit)) {
                if (session.current.site.preferences.paging_limit <= maxPagingLimit) {
                    limit = session.current.site.preferences.paging_limit;
                }
            }
        }
    }

    const hook = props.apiHook();
    const [params, setParams] = useState<P>(props.parameters);
    const [records, setRecords] = useState<R[]>([]);

    const { ref, inView } = useInView({
        threshold: 0.5,
    });


    let endOfRecords = false;
    let isEmptyRow = false;

    if (hook.data) {
        endOfRecords = hook.data.length < limit;


        if (hook.data.length == 1) {
            isEmptyRow = Object.values(hook.data[0] as Record<string, unknown>).filter((v) => {
                return v != null;
            }).length == 0;
            if (isEmptyRow == true) {
                endOfRecords = true;
            }
        } else if (records.length == 1) {
            isEmptyRow = Object.values(records[0] as Record<string, unknown>).filter((v) => {
                return v != null;
            }).length == 0;
            if (isEmptyRow == true) {
                endOfRecords = true;
            }
        }
    }

    const fetchMore = () => {
        if (hook.data != null) {
            endOfRecords = hook.data.length < limit;
            if (hook.data.length < limit) {
                return;
            }
        }


        let currentOffset = 0;
        let paramsWithPaging: IParametersWithPaging = params;

        if (paramsWithPaging.paging) {
            if (paramsWithPaging.paging.offset != null) {
                currentOffset = paramsWithPaging.paging.offset;
            }
        }

        currentOffset = currentOffset + limit;

        paramsWithPaging = {
            ...params,
            paging: {
                limit: limit,
                offset: currentOffset
            }
        }
        setParams(paramsWithPaging as P);
    };


    if (props.paged == false) {
        endOfRecords = true;
    }

    let cancelled = false;

    useEffect(() => {
        (async () => {
            hook.send(session.current.context, params, (results: R[] | null) => {

                if (cancelled == true) {
                    return;
                }
                if (results != null) {
                    if (results.length > 0) {
                        const paramsWithPaging: IParametersWithPaging = params;
                        if (paramsWithPaging.paging == null && props.maintainRecordsAfterParamChange == true) {
                            setRecords([...results]);
                        } else {
                            setRecords([...records, ...results]);
                        }

                    }
                }
            });
            if (cancelled == true) {
                return;
            }
        })();
        return () => {
            cancelled = true;
        }
    }, [params]);

    useEffect(() => {
        if (endOfRecords == false && inView == true && (hook.status == RequestStatus.loaded || hook.status == RequestStatus.idle)) {
            fetchMore();
        }
    }, [endOfRecords, inView, hook.status]);


    useEffect(() => {
        if (props.maintainRecordsAfterParamChange != true) {
            setRecords([]);
        }

        setParams(props.parameters);
    }, [props.parameters]);


    const tvc = useContext(TreeViewContext);

    const command = tvc.state == null ? null : tvc.state.command;

    const [keyRef, setKeyRef] = useState(0);

    const [updatedRefs, setUpdatedRefs] = useState<number[]>([]);

    useEffect(() => {
        if (tvc.state) {
            if (command) {
                if (command.command == TreeViewCommand.moveUp || command.command == TreeViewCommand.moveDown) {
                    if (command.targetId != null) {
                        for (let i = 0; i < records.length; i++) {
                            const currentRecord: ITreeViewRecord = (records[i] as unknown) as ITreeViewRecord;
                            if (currentRecord) {
                                if (currentRecord.id == command.targetId) {

                                    const srcIndex = i;
                                    const destIndex = command.command == TreeViewCommand.moveUp ? i - 1 : i + 1;

                                    const srcRecord: ITreeViewRecord = (records[srcIndex] as unknown) as ITreeViewRecord;
                                    const destRecord: ITreeViewRecord = (records[destIndex] as unknown) as ITreeViewRecord;

                                    if (destRecord) {
                                        const recCopy: R[] = [...records];
                                        recCopy[srcIndex] = (destRecord as unknown) as R;
                                        recCopy[destIndex] = (srcRecord as unknown) as R;
                                        setRecords([...recCopy]);
                                        setKeyRef(keyRef + 1);
                                        setUpdatedRefs([srcIndex, destIndex]);

                                        if (tvc.setState) {
                                            tvc.setState({
                                                ...tvc.state,
                                                command: null,
                                                selectedRecord: (records[i] as unknown) as ITreeViewRecord
                                            });
                                        }
                                    }

                                }

                            }

                        }
                    }
                }

            }
        }

    }, [command]);

    const animate = props.animate != false;

    const detector = <div style={{ height: 1, width: 1, position: "sticky", left: 0 }} ref={ref}></div>;

    const content = records.map((item, index) => {
        let key = index.toString();
        if (updatedRefs.indexOf(index) != -1) {
            key = `K${keyRef}-${index}`;
        }

        if (isEmptyRow == true && index == records.length - 1) {
            return <Fragment key={key}></Fragment>
        }

        let animateDelay = 0.06;
        if (props.animateDelay != null) {
            animateDelay = props.animateDelay;
        }
        let offset = 0.2;

        if (hook.data) {
            const pos = records.length - hook.data.length;
            if (index > pos) {
                offset += (index - pos) * animateDelay;
            }
        }

        return <Fragment key={index}>
            {animate
                ? (
                    <AnimateFrom key={index} delay={offset} style={{ opacity: 0, y: 50 }}>
                        <Fragment key={key}>
                            {props.renderItem(item, index)}
                        </Fragment>
                    </AnimateFrom>)
                : (
                    props.renderItem(item, index)
                )}
        </Fragment>;
    });

    const header = props.header != null && props.header(hook, records);

    const footer = <Fragment>
        {hook.status == RequestStatus.loading && (<DelayedProgressIndicator />)}
        {hook.status == RequestStatus.error && (
            <Fragment>
                <IconButton
                    onClick={() => {
                        setParams(JSON.parse(JSON.stringify(params)) as P);
                    }}>
                    <FiAlertTriangle color="red" />
                </IconButton>
                <span>&nbsp;{(props.showError == true && hook.err != null) && ErrorToString(hook.err)}&nbsp;&nbsp;</span>
            </Fragment>)
        }
    </Fragment>;

    if (props.isTable) {
        if (endOfRecords && !records.length && hook.status === RequestStatus.loaded) {
            return <Fragment>{props.children}</Fragment>;
        }

        return <Fragment>
            <table style={{ width: "100%" }} cellPadding={0} cellSpacing={0}>
                <thead>{header}</thead>
                <tbody>{content}</tbody>
            </table>
            {footer}
            {(!endOfRecords && hook.status === RequestStatus.loaded) && (
                <Fragment>
                    {detector}
                </Fragment>
            )}
        </Fragment>
    }


    return <Fragment>

        {header}
        {content}
        {footer}
        {(endOfRecords == false && hook.status == RequestStatus.loaded) && (
            <Fragment>
                {detector}
            </Fragment>
        )}
        {(endOfRecords == true && records.length == 0 && hook.status == RequestStatus.loaded) && (
            props.children
        )}
    </Fragment >
}

export const PagedDataView = memo(component) as typeof component;
